import { Routes, Route } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import StateTaxClientsPage from './pages/StateTaxClientsPage';
import StateTaxClientDetailPage from './pages/StateTaxClientDetailPage';
import ClientObligationsPage from './pages/ClientObligationsPage';
import ComplianceTasksPage from './pages/ComplianceTasksPage';
import ComplianceTaskDetailPage from './pages/ComplianceTaskDetailPage';
import ComplianceDashboardPage from './pages/ComplianceDashboardPage';
import ManagementPage from './pages/ManagementPage';
import ConversionPage from './pages/ConversionPage';
import PatchNotesPage from './pages/PatchNotesPage';
import QuickStartPage from './pages/QuickStartPage';

const useStyles = makeStyles({
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
});

// Dev-only escape hatch: set VITE_AUTH_BYPASS=true in .env.local to skip the MSAL
// auth gate so the UI can be previewed before the Azure AD app registration exists.
// Backend endpoints are already AuthorizationLevel.Anonymous, so requests go through
// without a Bearer token. Never set this in a production env.
const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';

export default function App() {
  const styles = useStyles();
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (!AUTH_BYPASS && inProgress !== InteractionStatus.None) {
    // Also what a person sees during the one silent sign-in attempt (STX-PROD-02).
    return (
      <div className={styles.loadingWrapper}>
        <Spinner size="large" label={t('loginPage.signingIn')} />
      </div>
    );
  }

  if (!AUTH_BYPASS && !isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<ComplianceDashboardPage />} />
        <Route path="/clients" element={<StateTaxClientsPage />} />
        <Route path="/clients/:id" element={<StateTaxClientDetailPage />} />
        <Route
          path="/compliance/clients/:clientId/obligations"
          element={<ClientObligationsPage />}
        />
        <Route path="/compliance/tasks" element={<ComplianceTasksPage />} />
        <Route path="/compliance/tasks/:id" element={<ComplianceTaskDetailPage />} />
        <Route path="/conversion" element={<ConversionPage />} />
        <Route path="/management" element={<ManagementPage />} />
        <Route path="/patch-notes" element={<PatchNotesPage />} />
        <Route path="/quick-start" element={<QuickStartPage />} />
      </Routes>
    </AppLayout>
  );
}
